import { flattenObject } from '@sitecore-cloudsdk/utils';
import { BaseEvent } from '../base-event';
import { MAX_EXT_ATTRIBUTES, UTM_PREFIX } from '../consts';
import { ErrorMessages } from '../../consts';
export class PageViewEvent extends BaseEvent {
    /**
     * A class that extends from {@link BaseEvent} and has all the required functionality to send a VIEW event
     * @param args - Unified object containing the required properties
     */
    constructor(args) {
        const { channel, currency, language, page } = args.eventData;
        super({
            channel,
            currency,
            language,
            page,
        }, args.id);
        this.extensionData = {};
        this.eventData = args.eventData;
        this.sendEvent = args.sendEvent;
        this.settings = args.settings;
        this.urlSearchParams = new URLSearchParams(decodeURI(args.searchParams));
        if (args.extensionData)
            this.extensionData = flattenObject({ object: args.extensionData });
        const numberOfExtensionDataProperties = Object.entries(this.extensionData).length;
        if (numberOfExtensionDataProperties > MAX_EXT_ATTRIBUTES)
            throw new Error(ErrorMessages.IV_0005);
        this.includeUTMParameters =
            args.eventData.includeUTMParameters === undefined ? true : args.eventData.includeUTMParameters;
    }
    /**
     * Gets the variant ID from the url if not passed by the developer
     * Gets the variant ID from the extension data if not found from the url
     * @returns - variant ID or null
     */
    getPageVariantId(pageVariantIdFromEventData, pageVariantIdFromExt) {
        if (pageVariantIdFromEventData)
            return pageVariantIdFromEventData;
        const pageVariantIdFromURL = this.urlSearchParams.get('variantid');
        if (pageVariantIdFromURL)
            return pageVariantIdFromURL;
        if (pageVariantIdFromExt)
            return pageVariantIdFromExt;
        return null;
    }
    /**
     * Returns the referrer if exists on page view event else null if we are on server and no referrer is on event, else
     * returns the href if on client side and the document referrer is different from the window location hostname
     * @returns - the referrer
     */
    getReferrer() {
        if (this.eventData.referrer)
            return this.eventData.referrer;
        if (typeof window === 'undefined')
            return null;
        if (!PageViewEvent.isFirstPageView || !document.referrer)
            return null;
        const { hostname, href } = new URL(document.referrer);
        return window.location.hostname !== hostname ? href : null;
    }
    /**
     * Maps parameters given as input to corresponding attributes send to the API
     * @returns the mapped object to be sent as payload
     */
    mapAttributes() {
        let viewPayload = {
            type: 'VIEW',
        };
        const pageVariantId = this.getPageVariantId(this.eventData.pageVariantId, this.extensionData['pageVariantId']);
        if (pageVariantId !== null)
            viewPayload.ext = { ...viewPayload.ext, pageVariantId };
        if (Object.keys(this.extensionData).length > 0) {
            delete this.extensionData['pageVariantId'];
            viewPayload.ext = { ...viewPayload.ext, ...this.extensionData };
        }
        if (this.includeUTMParameters) {
            const utmParameters = this.getUTMParameters();
            viewPayload = { ...viewPayload, ...utmParameters };
        }
        const referrer = this.getReferrer();
        if (referrer !== null)
            viewPayload = { ...viewPayload, referrer };
        return viewPayload;
    }
    /**
     * Sends the event to Sitecore EP
     * @returns - A promise that resolves with either the Sitecore EP response object or null
     */
    async send() {
        const baseAttr = this.mapBaseEventPayload();
        const eventAttrs = this.mapAttributes();
        const fetchBody = Object.assign({}, eventAttrs, baseAttr);
        PageViewEvent.isFirstPageView = false;
        return await this.sendEvent(fetchBody, this.settings);
    }
    /**
     * Retrieves UTM parameters from the url query string
     * @returns - an object containing the UTM parameters if they exist
     */
    getUTMParameters() {
        const utmParameters = {};
        this.urlSearchParams.forEach((value, key) => {
            const param = key.toLowerCase();
            if (param.indexOf(UTM_PREFIX) === 0)
                utmParameters[param] = value;
        });
        return utmParameters;
    }
}
PageViewEvent.isFirstPageView = true;
